import React, {useState} from "react";
import {Link} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../Recoil/Atom/authState";
import {useGetQuery} from "../../hooks/useQueryHooks";
import {ResponseList} from "../../models/clientModels/interfaces/Response";
import {
  EQWorkOrder,
  WorkOrderListParams,
  getSerialNumber,
  initializeWorkOrderListParams,
} from "../../models/serverModels/workOrder/EQWorkOrder";

import * as workOrderApi from "../../api/workOrderApi/workOrderApi";

import {Controller, useForm} from "react-hook-form";

import {
  Box,
  Button,
  Pagination,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import ActionButtonGroup from "../../components/ActionButtonGroup";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {workOrderRecordActionButton} from "../../models/clientModels/data/actionButtonData";
import {FilterGroup, StyledTable, SubMenuGroup} from "../../styledComponents/StyledListContainer";
import {PaginationContainer} from "../../styledComponents/StyledPagination";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1700px",
}));

const WorkOrderRecordList = () => {
  const tokenState = useRecoilValue(tokenSelector);
  const setAlertState = useSetRecoilState(alertStateAtom);

  const [page, setPage] = useState<number>(1);

  const [filter, setFilter] = useState<WorkOrderListParams>(initializeWorkOrderListParams);

  //note 점검·정비 발신·수신 목록
  const {data, isLoading, isFetching} = useGetQuery<ResponseList<EQWorkOrder>>({
    key: ["WorkOrderList", filter],
    api: () => workOrderApi.RecordList({...filter, token: tokenState}),
  });

  const {control, reset, setValue} = useForm({});

  //fix
  const recordListFilteringHandler = (
    event: React.BaseSyntheticEvent<Event, EventTarget & HTMLInputElement>
  ) => {
    setValue(event.target.name, event.target.value);
    setFilter({...filter, page: 1, [event.target.name]: event.target.value});
  };

  const resetHandler = () => {
    setFilter(initializeWorkOrderListParams);
    reset();
  };

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilter({...filter, page: value});
    setPage(value);
  };

  return (
    <ListContainer>
      <SubTitle subTitle="점검·정비 발신·수신 목록" />
      <SubMenuGroup>
        <FilterGroup>
          <Box>
            <Controller
              name="searchWord"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="작성자 또는 설비번호"
                  size="small"
                  name="creator"
                  onChange={recordListFilteringHandler}
                />
              )}
            />
          </Box>
          <Button
            onClick={resetHandler}
            variant="contained"
            color="success"
            size="small"
            sx={{whiteSpace: "nowrap"}}
          >
            초기화
          </Button>
        </FilterGroup>
        <ActionButtonGroup data={workOrderRecordActionButton(tokenState)} />
      </SubMenuGroup>
      <StyledTableContainer>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <TableCell rowSpan={2} align="center">
                일련번호
              </TableCell>
              <TableCell rowSpan={2} align="center">
                요청일자
              </TableCell>
              <TableCell rowSpan={2} align="center">
                작성부서
              </TableCell>
              <TableCell rowSpan={2} align="center">
                작성자
              </TableCell>
              <TableCell rowSpan={2} align="center">
                설비번호
              </TableCell>
              <TableCell rowSpan={2} align="center">
                설비명
              </TableCell>
              <TableCell rowSpan={2} align="center">
                설비등급
              </TableCell>
              <TableCell rowSpan={2} align="center">
                작업내용
              </TableCell>
              <TableCell rowSpan={2} align="center">
                협력업체명
              </TableCell>
              <TableCell rowSpan={2} align="center">
                변경사항
              </TableCell>
              <TableCell rowSpan={2} align="center">
                작업위험성평가
              </TableCell>
              <TableCell colSpan={2} align="center">
                교육
              </TableCell>
              <TableCell colSpan={8} align="center">
                작업허가서발행
              </TableCell>
              <TableCell rowSpan={2} align="center">
                가동전점검
              </TableCell>
              <TableCell rowSpan={2} align="center">
                자료등록
              </TableCell>
              <TableCell rowSpan={2} align="center">
                비고
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center">근로자</TableCell>
              <TableCell align="center">작업자</TableCell>
              <TableCell align="center" width="2%">
                화기
              </TableCell>
              <TableCell align="center" width="2%">
                일반
              </TableCell>
              <TableCell align="center" width="2%">
                정전
              </TableCell>
              <TableCell align="center" width="2%">
                고소
              </TableCell>
              <TableCell align="center" width="2%">
                밀폐
              </TableCell>
              <TableCell align="center" width="2%">
                굴착
              </TableCell>
              <TableCell align="center" width="2%">
                중장비
              </TableCell>
              <TableCell align="center" width="2%">
                방사선
              </TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={15} column={24} />
            ) : (
              data?.items.map((item) => (
                <TableRow key={item?.uid}>
                  <TableCell align="center">
                    <Link to={`/WorkOrder/View/${item.uid}`}>{getSerialNumber(item)}</Link>
                  </TableCell>
                  <TableCell align="center">{`${new Date(item?.requestDate).getFullYear()}년 ${
                    new Date(item?.entryDate).getMonth() + 1
                  }월 ${new Date(item?.entryDate).getDate()}일`}</TableCell>
                  <TableCell align="center">{item.department}</TableCell>
                  <TableCell align="center">{item.writerName}</TableCell>
                  <TableCell align="center">{item.specification?.identifyNumber}</TableCell>
                  <TableCell align="center">
                    <Link to={`/Equipment/Detail/${item.specification.uid}`}>
                      {item.specification?.name}
                    </Link>
                  </TableCell>
                  <TableCell align="center">{item.specification?.eqLevel}</TableCell>
                  <TableCell align="center">{item.requestContent}</TableCell>
                  <TableCell align="center">{item.repairCompany}</TableCell>
                  <TableCell align="center">
                    {item.changeRequestUID === 0 ? "해당없음" : <Link to={``}>{"해당링크"}</Link>}
                  </TableCell>
                  <TableCell align="center">
                    {item?.evaluation2 ? (
                      <Link to={``}>
                        {new Date(item?.evaluation2).getFullYear()}년
                        {new Date(item?.evaluation2).getMonth() + 1}월
                        {new Date(item?.evaluation2).getDate()}일
                      </Link>
                    ) : (
                      "해당없음"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item?.education2 ? (
                      <Link to={``}>
                        {new Date(item?.education2).getFullYear()}년
                        {new Date(item?.education2).getMonth() + 1}월
                        {new Date(item?.education2).getDate()}일
                      </Link>
                    ) : (
                      "해당없음"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item?.education1 ? (
                      <Link to={``}>
                        {new Date(item?.education1).getFullYear()}년
                        {new Date(item?.education1).getMonth() + 1}월
                        {new Date(item?.education1).getDate()}일
                      </Link>
                    ) : (
                      "해당없음"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {item.fireWorkFile ? <Link to={``}>O</Link> : "X"}
                  </TableCell>
                  <TableCell align="center">
                    {item.generalFile ? <Link to={``}>O</Link> : "X"}
                  </TableCell>
                  <TableCell align="center">
                    {item.isStopElectricity ? <Link to={``}>O</Link> : "X"}
                  </TableCell>
                  <TableCell align="center">
                    {item.isHeight ? <Link to={``}>O</Link> : "X"}
                  </TableCell>
                  <TableCell align="center">
                    {item.isClosed ? <Link to={``}>O</Link> : "X"}
                  </TableCell>
                  <TableCell align="center">
                    {item.isExcavation ? <Link to={``}>O</Link> : "X"}
                  </TableCell>
                  <TableCell align="center">
                    {item.isHeavy ? <Link to={``}>O</Link> : "X"}
                  </TableCell>
                  <TableCell align="center">
                    {item.isRadiation ? <Link to={``}>O</Link> : "X"}
                  </TableCell>
                  <TableCell align="center">
                    {item.preCheckFile ? <Link to={``}>O</Link> : "해당없음"}
                  </TableCell>
                  <TableCell align="center">{item.attachment}</TableCell>
                  <TableCell align="center">{item.externalComment}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>

      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={data?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default WorkOrderRecordList;
